import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  CheckCircle,
  AlertCircle,
  Folder,
  Plus,
} from "lucide-react";
import { CreateProjectData, UpdateProjectData, ProjectAPI, Project } from "@/api/projects";
import { CreateApplicationData, ApplicationAPI } from "@/api/applications";
import { useApplicationList } from "@/hooks/useApplications";
import { TECHNOLOGY_STACKS, DEVELOPMENT_PHASE_OPTIONS, APPLICATION_TYPE_OPTIONS } from "@/helpers/collections";
import { Badge } from "./ui/badge";
import { NewScan } from "./NewScan";

interface ProjectFormProps {
  isOpen: boolean;
  onClose: () => void;
  workgroupId: number;
  applicationId?: number;
  project?: Project | null;
  onSuccess?: () => void;
}

export function ProjectForm({
  isOpen,
  onClose,
  workgroupId,
  applicationId,
  project = null,
  onSuccess
}: ProjectFormProps) {
  const isEditMode = !!project;

  const { data: organisationApplications } = useApplicationList(workgroupId);

  const [projectData, setProjectData] = useState({
    name: "",
    description: "",
    selectedApplicationId: applicationId ? applicationId.toString() : "",
    technology: [] as string[],
    applicationName: "",
    applicationDescription: "",
    applicationPhase: "",
    applicationType: "",
    businessCriticality: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen && project) {
      setProjectData({
        name: project.name || "",
        description: project.description || "",
        selectedApplicationId: project.applicationid?.toString() || "",
        technology: project.technology ? (typeof project.technology === 'string' ? JSON.parse(project.technology) : project.technology) : [],
        applicationName: "",
        applicationDescription: "",
        applicationPhase: "",
        applicationType: "",
        businessCriticality: "",
      });
      setError(null);
      setIsComplete(false);
      setValidationErrors({});
    } else if (isOpen && !project) {
      setProjectData({
        name: "",
        description: "",
        selectedApplicationId: applicationId ? applicationId.toString() : "",
        technology: [],
        applicationName: "",
        applicationDescription: "",
        applicationPhase: "",
        applicationType: "",
        businessCriticality: "",
      });
      setError(null);
      setIsComplete(false);
      setValidationErrors({});
    }
  }, [isOpen, project, applicationId]);

  const toggleArrayValue = (field: 'technology', value: string) => {
    setProjectData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };
  const updateProjectData = (field: string, value: string) => {
    setProjectData(prev => ({
      ...prev,
      [field]: value
    }));
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    if (error) {
      setError(null);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!projectData.name.trim()) {
      errors.name = "Project name is required";
    }

    if (!projectData.selectedApplicationId) {
      errors.selectedApplicationId = "Application selection is required";
    }

    if (projectData.selectedApplicationId === 'custom') {
      if (!projectData.applicationName.trim()) {
        errors.applicationName = "Application name is required";
      }
      if (!projectData.applicationType) {
        errors.applicationType = "Application type is required";
      }
      if (!projectData.businessCriticality) {
        errors.businessCriticality = "Business criticality is required";
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const createNewApplication = async () => {
    if (projectData.selectedApplicationId !== 'custom') return null;

    try {
      const applicationData: CreateApplicationData = {
        workgroupid: workgroupId,
        name: projectData.applicationName,
        description: projectData.applicationDescription,
        phase: projectData.applicationPhase,
        applicationtype: projectData.applicationType,
        businesscriticality: projectData.businessCriticality,
        isactive: true
      };

      const response = await ApplicationAPI.create(applicationData);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleCreateProject = async (shouldOpenScan = false) => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      let applicationIdToUse = null;
      if (projectData.selectedApplicationId === 'custom') {
        const createdApplication = await createNewApplication();
        applicationIdToUse = createdApplication?.id;
        if (!applicationIdToUse) {
          throw new Error('Failed to create application');
        }
      } else {
        applicationIdToUse = parseInt(projectData.selectedApplicationId);
      }

      if (!applicationIdToUse) {
        throw new Error('Failed to get or create application');
      }

      const projectPayload: CreateProjectData = {
        workgroupid: workgroupId,
        applicationid: applicationIdToUse,
        name: projectData.name.trim(),
        description: projectData.description.trim(),
        technology: JSON.stringify(projectData.technology),
      };

      const response = await ProjectAPI.create(projectPayload);

      if (response.data) {
        setIsSubmitting(false);
        setIsComplete(true);
        setCreatedProjectId(response.data.id);

        if (shouldOpenScan) {
          setTimeout(() => {
            setScanModalOpen(true);
          }, 1500);
        } else {

          setTimeout(() => {
            onClose();
            setIsComplete(false);
            setProjectData({
              name: "",
              description: "",
              selectedApplicationId: applicationId ? applicationId.toString() : "",
              technology: [],
              applicationName: "",
              applicationDescription: "",
              applicationPhase: "",
              applicationType: "",
              businessCriticality: "",
            });

            if (onSuccess) {
              onSuccess();
            }
          }, 2000);
        }



        return;
      } else {
        throw new Error(response.message || 'Failed to create project');
      }
    } catch (error) {
      setIsSubmitting(false);
      setError(error instanceof Error ? error.message : 'Failed to create project. Please try again.');
    }
  };

  const handleUpdateProject = async (shouldOpenScan = false) => {
    if (!validateForm() || !project) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const projectPayload: UpdateProjectData = {
        applicationid: parseInt(projectData.selectedApplicationId),
        workgroupid: workgroupId,
        name: projectData.name.trim(),
        description: projectData.description.trim(),
        technology: JSON.stringify(projectData.technology),
      };

      const response = await ProjectAPI.update(project.id, projectPayload);

      if (response.data) {
        setIsSubmitting(false);
        setIsComplete(true);
        setCreatedProjectId(project.id);

        if (shouldOpenScan) {
          setTimeout(() => {
            setScanModalOpen(true);
          }, 1500);
        } else {
          if (onSuccess) {
            onSuccess();
          }
          onClose();
          setIsComplete(false);
        }
        return;
      } else {
        throw new Error(response.message || 'Failed to update project');
      }
    } catch (error) {
      setIsSubmitting(false);
      setError(error instanceof Error ? error.message : 'Failed to update project. Please try again.');
    }
  };

  const handleSubmit = (e: React.FormEvent, shouldOpenScan = false) => {
    e.preventDefault();
    if (isEditMode) {
      handleUpdateProject(shouldOpenScan);
    } else {
      handleCreateProject(shouldOpenScan);
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    setValidationErrors({});
    setScanModalOpen(false);
    setCreatedProjectId(null);
    onClose();
  };

  const handleScanModalClose = () => {
    setScanModalOpen(false);
    setCreatedProjectId(null);

    if (onSuccess) {
      onSuccess();
    }

    setTimeout(() => {
      onClose();
      setIsComplete(false);
      setProjectData({
        name: "",
        description: "",
        selectedApplicationId: applicationId ? applicationId.toString() : "",
        technology: [],
        applicationName: "",
        applicationDescription: "",
        applicationPhase: "",
        applicationType: "",
        businessCriticality: "",
      });
    }, 300);
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Project {isEditMode ? 'Updated' : 'Created'} Successfully!
          </h3>
          <p className="text-muted-foreground">
            {isEditMode
              ? `The project "${projectData.name}" has been updated with your changes.`
              : `Your new project "${projectData.name}" has been created and is ready for scanning.`
            }
          </p>
        </div>
      );
    }

    if (isSubmitting) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {isEditMode ? 'Updating Project...' : 'Creating Project...'}
          </h3>
          <p className="text-muted-foreground">
            {isEditMode
              ? 'Saving your changes...'
              : 'Setting up your project repository and preparing for security scanning...'
            }
          </p>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="application">Select Application *</Label>
            <Select
              value={projectData.selectedApplicationId}
              onValueChange={(value) => {
                const application = organisationApplications?.find(app => app.id.toString() === value);
                setProjectData(prev => ({
                  ...prev,
                  selectedApplicationId: value,
                  applicationName: application?.name || '',
                  applicationDescription: application?.description || ''
                }));

                if (validationErrors.selectedApplicationId) {
                  setValidationErrors(prev => ({ ...prev, selectedApplicationId: '' }));
                }
              }}
            >
              <SelectTrigger className={`rounded-lg border-border ${validationErrors.selectedApplicationId ? 'border-red-300' : ''
                }`}>
                <SelectValue placeholder="Choose an application..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">
                  <div className="flex items-center space-x-2">
                    <Plus className="w-4 h-4" />
                    <span>Create New Application</span>
                  </div>
                </SelectItem>
                {organisationApplications && organisationApplications.length > 0 ? (
                  organisationApplications.map((application) => (
                    <SelectItem key={application.id} value={application.id.toString()}>
                      {application.name}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none" disabled>No applications found</SelectItem>
                )}
              </SelectContent>
            </Select>
            {validationErrors.selectedApplicationId && (
              <p className="text-sm text-red-600">{validationErrors.selectedApplicationId}</p>
            )}
            <p className="text-sm text-muted-foreground">
              Select an existing application or choose "Create New Application" to add one.
            </p>
          </div>

          {projectData.selectedApplicationId === 'custom' && (
            <div className="space-y-4 p-4 border border-border rounded-lg bg-muted dark:bg-gray-800/50 dark:border-gray-600">
              <h4 className="font-medium text-foreground dark:text-gray-100">New Application Details</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="applicationName">Application Name *</Label>
                  <Input
                    id="applicationName"
                    placeholder="Enter application name"
                    value={projectData.applicationName}
                    onChange={(e) => updateProjectData('applicationName', e.target.value)}
                    className="rounded-lg border-border"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="applicationPhase">Development Phase</Label>
                  <Select
                    value={projectData.applicationPhase}
                    onValueChange={(value) => updateProjectData('applicationPhase', value)}
                  >
                    <SelectTrigger className="rounded-lg border-border">
                      <SelectValue placeholder="Select phase..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DEVELOPMENT_PHASE_OPTIONS.map((phase) => (
                        <SelectItem key={phase.value} value={phase.value}>
                          {phase.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="applicationType">Application Type *</Label>
                  <Select
                    value={projectData.applicationType}
                    onValueChange={(value) => updateProjectData('applicationType', value)}
                  >
                    <SelectTrigger className="rounded-lg border-border">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {APPLICATION_TYPE_OPTIONS.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessCriticality">Business Criticality *</Label>
                  <Select
                    value={projectData.businessCriticality}
                    onValueChange={(value) => updateProjectData('businessCriticality', value)}
                  >
                    <SelectTrigger className="rounded-lg border-border">
                      <SelectValue placeholder="Select criticality..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="applicationDescription">Description</Label>
                <Textarea
                  id="applicationDescription"
                  placeholder="Describe the application's purpose and functionality..."
                  value={projectData.applicationDescription}
                  onChange={(e) => updateProjectData('applicationDescription', e.target.value)}
                  className="rounded-lg border-border"
                  rows={3}
                />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="name">Project Name *</Label>
            <Input
              id="name"
              type="text"
              value={projectData.name}
              onChange={(e) => updateProjectData('name', e.target.value)}
              placeholder="Enter project name"
              className={`0 ${validationErrors.name ? 'border-red-300' : ''}`}
              maxLength={100}
              required
            />
            {validationErrors.name && (
              <p className="text-sm text-red-600">{validationErrors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={projectData.description}
              onChange={(e) => updateProjectData('description', e.target.value)}
              placeholder="Describe your project"
              className="min-h-[100px]"
            />
          </div>


        </div>
        <div>
          <Label>Technology Stack</Label>
          <p className="text-sm text-muted-foreground mb-3">Select technologies used in this application</p>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border rounded-lg p-3">
            {TECHNOLOGY_STACKS.map((tech) => (
              <Badge
                key={tech.value}
                variant={projectData.technology.includes(tech.label) ? "default" : "outline"}
                className={`cursor-pointer transition-all ${projectData.technology.includes(tech.label)
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-gray-100'
                  }`}
                onClick={() => toggleArrayValue('technology', tech.label)}
              >
                {tech.label}
              </Badge>
            ))}
          </div>
        </div>
      </form>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Folder className="w-5 h-5 text-primary" />
              <span>{isEditMode ? 'Edit Project' : 'Create New Project'}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            {renderContent()}
          </div>

          {!isSubmitting && !isComplete && (
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={(e) => handleSubmit(e, false)}
                disabled={Object.values(validationErrors).some(Boolean)}
                className="bg-primary hover:bg-primary/90"
              >
                {isEditMode ? 'Update Project' : 'Create Project'}
              </Button>

              <Button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={Object.values(validationErrors).some(Boolean)}

                className="bg-[#111] hover:bg-[#111]/90"
              >
                {isEditMode ? 'Update & Start Scan' : 'Create & Start Scan'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {createdProjectId && (
        <NewScan
          isOpen={scanModalOpen}
          onClose={handleScanModalClose}
          workgroupId={workgroupId}
          applicationId={parseInt(projectData.selectedApplicationId)}
          projectId={createdProjectId}
        />
      )}
    </>
  );
}
